// Logging a set (walkthrough 5), as rules: what the logging sheet starts
// with — and so what a one-tap Log logs — and how the sheet's typed text
// becomes the API's TrainingSetWrite. Kept out of the component files
// (react-refresh) and pinned by logSet.test.js.
import { parseDecimal } from "../../../lib/decimal";
import { findGuidance, formatLoad, formatNumber, formatRir, setLine } from "./format";
import {
  clampDumbbell,
  convert,
  loadStep,
  nextSetSuggestion,
  roundToStep,
  setupFor,
} from "./steps";

// backend/training/constants.py MAX_LOAD, MAX_REPS, MAX_RIR.
export const MAX_LOAD = 2000;
export const MAX_REPS = 100;
export const MAX_RIR = 5;
// How long an armed "Delete this set?" waits before it puts itself away.
export const DISARM_MS = 5000;

const has = (value) => value !== null && value !== undefined && value !== "";
const num = (value) => (has(value) && Number.isFinite(Number(value)) ? Number(value) : null);
const round2 = (value) => Math.round(value * 100) / 100;

/** external (a weight), bodyweight (added weight or a band, D17) or band. */
export const loadKindOf = (exercise) => exercise?.load_kind ?? "external";

const PICK = { band: "a band", bodyweight: "a setup", external: "one" };

/**
 * Finding the weight before the first set: always what to pick (about N
 * reps with R in reserve). The engine's own reason already says it; any
 * other reason (an exercise swapped in or added by you: "No weight for X in
 * this plan…") gets it after.
 */
function findNote(prefill, item, exercise) {
  const kind = loadKindOf(exercise);
  const reps = prefill.reps ?? "the target";
  const rir = formatRir(prefill.rir) ?? "a couple";
  const why = item?.why?.load;
  if (!why || /^Find your /.test(why)) return why ?? findGuidance(kind, reps, rir);
  const verb = kind === "external" ? "lift" : "do";
  return `${why} Pick ${PICK[kind]} you could ${verb} about ${reps} times with ${rir} in reserve.`;
}

/** D9: the plan has no number for this item yet — the first set finds it. */
export function isFinding(item, exercise) {
  if (!item) return false;
  if (item.find_weight === true) return true;
  return !has(item.load) && loadKindOf(exercise) !== "band" && !item.band;
}

/** The settings' bands with their help in `units` (bands are in the equipment block's units). */
function bandsIn(settings, units) {
  const from = settings?.equipment?.units ?? settings?.units;
  return (settings?.bands ?? [])
    .filter((band) => num(band?.assist) !== null)
    .map((band) => ({ name: band.name, assist: convert(Number(band.assist), from, units) }));
}

/** The bodyweight a set was done at (its snapshot, else the settings'), in `units`; null unknown. */
function bodyweightOf(set, settings, units) {
  const own = num(set?.bodyweight);
  if (own !== null) return own;
  if (num(settings?.bodyweight) === null) return null;
  return convert(Number(settings.bodyweight), settings.units, units);
}

/**
 * The help a set's band gave, in `units`: the logged snapshot, or — for a
 * set still queued — the band of that name in the settings. 0 without a
 * band; null when the band isn't known (nothing to build on).
 */
function bandHelpOf(set, settings, units) {
  if (!set?.band) return 0;
  const logged = num(set.band_assist);
  if (logged !== null) return logged;
  const name = String(set.band).trim().toLowerCase();
  const band = bandsIn(settings, units).find((b) => String(b.name).trim().toLowerCase() === name);
  return band ? band.assist : null;
}

const withMode = (values, exercise) => ({
  ...values,
  mode: loadKindOf(exercise) === "bodyweight" ? (values.band ? "band" : "added") : null,
});

/**
 * What the sheet starts with (walkthrough 5):
 * - a later set: the previous set of that exercise this session — or,
 *   while the weight is being found (D9), the next set's suggestion from
 *   that set (Epley at the target reps + reserve, on the load's step);
 * - the first set: the plan's target — its load (or band), target reps
 *   and reserve; finding the weight, no load at all;
 * - an exercise the plan doesn't have, before its first set: the bottom
 *   of its rep range, nothing else.
 *
 * `sets` are this session's sets of the exercise, oldest first (queued
 * ones included). `source` says which rule gave the numbers: "previous",
 * "suggestion", "plan", "find" or "none".
 *
 * @returns {{load: number|null, reps: number|null, rir: number|null,
 *   band: string, mode: "added"|"band"|null, source: string,
 *   from?: object, suggestion?: object}}
 */
export function prefillSet({ item, exercise, sets, settings, units = settings?.units ?? "lb" }) {
  const kind = loadKindOf(exercise);
  const last = sets?.length ? sets[sets.length - 1] : null;
  const targetReps = num(item?.target_reps) ?? num(item?.reps_low) ?? num(exercise?.rep_range?.[0]);
  const targetRir = num(item?.rir);
  const finding = isFinding(item, exercise);

  if (last) {
    const previous = {
      load: num(last.load),
      reps: num(last.reps),
      rir: num(last.rir),
      band: last.band ?? "",
      source: "previous",
      from: last,
    };
    // D9 within a session: the next set builds from the last one.
    if (finding && targetReps !== null && targetRir !== null) {
      const built = suggestFrom(last, { exercise, kind, targetReps, targetRir, settings, units });
      if (built) return withMode({ ...previous, ...built }, exercise);
    }
    return withMode(previous, exercise);
  }

  if (item) {
    const band = item.band ?? "";
    let load = null;
    if (!finding && kind !== "band") load = num(item.load) ?? 0;
    return withMode(
      { load, reps: targetReps, rir: targetRir, band, source: finding ? "find" : "plan" },
      exercise,
    );
  }
  return withMode({ load: null, reps: targetReps, rir: null, band: "", source: "none" }, exercise);
}

/**
 * The next set's numbers from the last one while finding the weight (D9),
 * as prefill fields — or null when the last set gives nothing to build on.
 * - a weight: Epley's suggestion on the step, never past the heaviest
 *   dumbbell (`suggestion.heaviest` says it stopped there);
 * - a bodyweight move: the effective load (bodyweight + added − a band's
 *   help) is what's estimated, and the setup nearest the target is picked
 *   (setupFor: a band, bodyweight or added weight). With no bands in the
 *   settings, a target under bodyweight can't be set up: source
 *   "needs-band" keeps the last set's numbers and says why;
 * - a band move has no load to build on.
 */
function suggestFrom(last, { exercise, kind, targetReps, targetRir, settings, units }) {
  const step = loadStep(exercise, settings, units);
  if (kind === "external") {
    const suggestion = nextSetSuggestion(last, targetReps, targetRir, step);
    if (!suggestion) return null;
    const load = clampDumbbell(suggestion.load, exercise, settings, units);
    const heaviest = load < suggestion.load;
    return {
      load,
      reps: suggestion.reps,
      rir: suggestion.rir,
      source: "suggestion",
      suggestion: { ...suggestion, load, ...(heaviest ? { heaviest: true } : {}) },
    };
  }
  if (kind !== "bodyweight") return null;
  const bodyweight = bodyweightOf(last, settings, units);
  const help = bandHelpOf(last, settings, units);
  if (!(bodyweight > 0) || help === null) return null;
  const suggestion = nextSetSuggestion(last, targetReps, targetRir, step, { offset: bodyweight - help });
  if (!suggestion) return null;
  const bands = bandsIn(settings, units);
  if (!bands.length && roundToStep(suggestion.target - bodyweight, step) < 0) {
    return { source: "needs-band", suggestion: { ...suggestion, help: bodyweight - suggestion.target } };
  }
  const setup = setupFor(suggestion.target, bodyweight, bands, step);
  return {
    load: setup.load,
    band: setup.band,
    reps: suggestion.reps,
    rir: suggestion.rir,
    source: "suggestion",
    suggestion: { ...suggestion, load: setup.load, band: setup.band, effective: setup.effective },
  };
}

/**
 * A plan item with its load in `toUnits` (the session's), when the plan
 * was made in `fromUnits` — rounded to the exercise's real step there, and
 * never past the heaviest dumbbell. The plan itself keeps its own units
 * (edits send the original); this is what the cards and the sheet show.
 */
export function planItemIn(item, fromUnits, toUnits, exercise, settings) {
  if (!item || !fromUnits || !toUnits || fromUnits === toUnits) return item;
  const load = num(item.load);
  if (load === null || load === 0) return item;
  const step = loadStep(exercise, settings, toUnits);
  const value = clampDumbbell(roundToStep(convert(load, fromUnits, toUnits), step), exercise, settings, toUnits);
  return { ...item, load: round2(value) };
}

/**
 * The sheet's fields (typed text) from a prefill or a logged set. A
 * bodyweight move is in "added" or "band" mode; a unilateral one has its
 * left/right reps on when the set has both.
 */
export function formFrom(values, exercise) {
  const kind = loadKindOf(exercise);
  const sides = has(values?.reps_left) && has(values?.reps_right);
  return {
    load: has(values?.load) ? formatNumber(values.load) : "",
    reps: has(values?.reps) ? String(values.reps) : "",
    rir: has(values?.rir) ? String(values.rir) : "",
    band: values?.band ?? "",
    mode: kind === "bodyweight" ? (values?.band ? "band" : "added") : null,
    sides,
    left: sides ? String(values.reps_left) : "",
    right: sides ? String(values.reps_right) : "",
  };
}

const wholeNumber = (text, max) => {
  const trimmed = String(text ?? "").trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= max ? value : null;
};

/**
 * The sheet's text as a TrainingSetWrite (without client_id — the queue
 * adds it), or the errors that stop it, by field: `load`, `reps`, `rir`,
 * `band`, `reps_left`, `reps_right`, in words that say what to type.
 *
 * - external: a load is required (0–2000, two decimals at most);
 * - bodyweight: "added" mode sends the added weight (empty = 0, just
 *   bodyweight) and clears the band; "band" mode needs a band, load 0;
 * - band: a band is required, no load;
 * - unilateral with left/right on: both are required and the set counts
 *   the weaker side's reps; off, both are cleared.
 *
 * @returns {{body: object, errors: Record<string, string>, ok: boolean}}
 */
export function buildSetBody(form, exercise) {
  const kind = loadKindOf(exercise);
  const errors = {};
  const body = { exercise: exercise.key };

  if (kind === "external" || (kind === "bodyweight" && form.mode !== "band")) {
    const added = kind === "bodyweight";
    const text = String(form.load ?? "").trim();
    const value = added && text === "" ? 0 : parseDecimal(text);
    if (value === null) {
      errors.load = added
        ? "Enter the weight you added — a number like 10 or 2.5 (0 for bodyweight only)."
        : "Enter the weight you used — a number like 135 or 62.5.";
    } else if (value < 0 || value > MAX_LOAD) {
      errors.load = `Between 0 and ${MAX_LOAD}.`;
    } else {
      body.load = round2(value);
    }
    if (added) body.band = "";
  } else {
    if (kind === "bodyweight") body.load = 0;
    if (!form.band) errors.band = "Choose the band you used.";
    else body.band = form.band;
  }

  if (exercise.unilateral && form.sides) {
    const left = wholeNumber(form.left, MAX_REPS);
    const right = wholeNumber(form.right, MAX_REPS);
    if (left === null) errors.reps_left = `Left side's reps — a whole number, 0–${MAX_REPS}.`;
    if (right === null) errors.reps_right = `Right side's reps — a whole number, 0–${MAX_REPS}.`;
    if (left !== null && right !== null) {
      body.reps = Math.min(left, right);
      body.reps_left = left;
      body.reps_right = right;
    }
  } else {
    const reps = wholeNumber(form.reps, MAX_REPS);
    if (reps === null) errors.reps = `Enter the reps you did — a whole number, 0–${MAX_REPS}.`;
    else body.reps = reps;
    if (exercise.unilateral) {
      body.reps_left = null;
      body.reps_right = null;
    }
  }

  const rirText = String(form.rir ?? "").trim();
  if (rirText === "") {
    body.rir = null;
  } else {
    const rir = wholeNumber(rirText, MAX_RIR);
    if (rir === null) errors.rir = `0–${MAX_RIR}: how many more you could have done (5 means 5 or more).`;
    else body.rir = rir;
  }

  return { body, errors, ok: Object.keys(errors).length === 0 };
}

/**
 * The set a one-tap Log logs: the prefill as it stands — but only when it
 * is a whole set. Finding the weight before the first set (no number yet),
 * a band move with no band, or nothing to go on: null (the sheet asks).
 */
export function quickSet(prefill, exercise) {
  if (!prefill || prefill.source === "find" || prefill.source === "none") return null;
  const { body, ok } = buildSetBody(formFrom(prefill, exercise), exercise);
  return ok ? body : null;
}

/**
 * Where the sheet's numbers came from, in words — so every number it
 * shows has its reason (spec §5.7).
 */
export function prefillNote(prefill, { item, exercise, units = "lb" } = {}) {
  if (!prefill) return "";
  const line = (set) => setLine(set, units, exercise);
  const bodyweight = loadKindOf(exercise) === "bodyweight";
  const finding = bodyweight ? "Finding your setup" : "Finding your weight";
  const estimate = (suggestion) =>
    `your last set (${line(prefill.from)}) puts your estimated max at ~${formatNumber(Math.round(suggestion.e1rm))} ${units}`;
  switch (prefill.source) {
    case "previous":
      return `As your last set: ${line(prefill.from)}. Change what was different.`;
    case "suggestion": {
      const { suggestion } = prefill;
      const rir = formatRir(suggestion.rir);
      let target;
      if (bodyweight) {
        target = setLine({ load: suggestion.load, band: suggestion.band, reps: 0 }, units, exercise).replace(/ × 0$/, "");
      } else {
        target = formatLoad(suggestion.load, units);
        if (suggestion.heaviest) target = `${target} (your heaviest dumbbell)`;
      }
      return `${finding}: ${estimate(suggestion)}, so try ${target} for ${suggestion.reps} with ${rir} in reserve.`;
    }
    case "needs-band": {
      const { suggestion } = prefill;
      return `${finding}: ${estimate(suggestion)}, so ${suggestion.reps} with ${formatRir(suggestion.rir)} in reserve needs about ${formatNumber(Math.round(suggestion.help))} ${units} of help — a band. No bands in your settings yet: add them in Settings to get one suggested. Until then, this repeats your last set; change what you need.`;
    }
    case "find":
      return findNote(prefill, item, exercise);
    case "plan":
      return item?.why?.load ? `From the plan: ${item.why.load}` : "From the plan.";
    default:
      return "Not in the plan — enter what you did.";
  }
}
